import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSelectChange } from '@angular/material/select';
import { ArcElement, BarController, BarElement, CategoryScale, Chart, Filler, Legend, LinearScale, LineController, LineElement, PieController, PointElement, Tooltip } from 'chart.js';
import { AbstractComponent, ChartSlice, DateUtil, ExportService, LeadCharts, PerDateChartItem, PerDayChartData, PeakHourChartItem, Pager, Tracker, TrackerOriginEnum, TrackerService, CacheKeyEnum } from 'lib-trend-core';
import { concatMap, debounceTime, distinctUntilChanged, Observable, Subject, Subscription, switchMap, takeUntil } from 'rxjs';

Chart.register([PieController, ArcElement, Tooltip, Legend, BarController, BarElement, CategoryScale, LinearScale, LineController, LineElement, PointElement, Filler]);

@Component({
    selector: 'app-tracking-report',
    // templateUrl: './tracking-report.component.html',
    templateUrl: './tracking-report.v2.component.html',
    styleUrls: ['./tracking-report.component.scss', './tracking-report.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TrackingReportComponent extends AbstractComponent implements OnInit, AfterViewInit {
  private termOfSearch: Subject<string> = new Subject<string>();
  private searchSubscription: Subscription | null = null;
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  @ViewChild('chartOrigin', { static: false }) chartOriginRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartMediaType', { static: false }) chartMediaTypeRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartCampaign', { static: false }) chartCampaignRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartPerDay', { static: false }) chartPerDayRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartPeakHour', { static: false }) chartPeakHourRef: ElementRef<HTMLCanvasElement>;

  pager: Pager<Tracker> = new Pager<Tracker>({ perPage: 10 });
  listObservable: Observable<Pager<Tracker>>;

  isPicker: boolean = false;
  isLoading: boolean = false;
  listOrigin: Array<TrackerOriginEnum> = Object.values(TrackerOriginEnum);

  originLabel: string = 'report.tracking.origin';
  pieChartByOrigin: Array<ChartSlice> = [];
  pieChartByMediaType: Array<ChartSlice> = [];
  pieChartByCampaign: Array<ChartSlice> = [];

  chartPerDayData: Array<PerDateChartItem> = [];
  chartPeakHourData: Array<PeakHourChartItem> = [];
  leadsYesterday: number = 0;

  private chartOriginInstance: Chart | null = null;
  private chartMediaTypeInstance: Chart | null = null;
  private chartCampaignInstance: Chart | null = null;
  private chartPerDayInstance: Chart | null = null;
  private chartPeakHourInstance: Chart | null = null;
  private chartReady: boolean = false;

  private readonly CHART_COLORS = ['#4361ee', '#0acf97', '#ffc35a', '#dc3545', '#fd7e14', '#6f42c1', '#20c997'];

  breadcrumb = [
    { href: '/tracking', label: 'report.tracking.breadcrumbTracking' },
    { label: 'report.tracking.breadcrumbReport' },
  ]

  constructor(
    injector: Injector,
    private trackerService: TrackerService,
    public exportService: ExportService,
    private cdr: ChangeDetectorRef,
  ) {
    super(injector);
    this.setupForm();
    this.cacheKey = CacheKeyEnum.TRACKER_LIST;
  }

  ngOnInit(): void {
    this.getTrackingReport();
    this.getPieChartsData();
    this.getchartPerDayData();
  }

  ngAfterViewInit(): void {
    const cached = this.restoreState<Tracker>();
    this.chartReady = true;
    if (this.pieChartByOrigin.length) {
      this.buildPieCharts();
    }
    if (this.chartPerDayData.length) {
      this.buildBarChart();
    }
    if (this.chartPeakHourData.length) {
      this.buildPeakHourChart();
    }

    if (cached) {
      this.pager = cached.pager;
      this.searchString = cached.search ?? '';
      this.searchParams = cached.params ?? {};
    }

    this.resolveFiltersCache();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.chartOriginInstance?.destroy();
    this.chartMediaTypeInstance?.destroy();
    this.chartCampaignInstance?.destroy();
    this.chartPerDayInstance?.destroy();
    this.chartPeakHourInstance?.destroy();
  }

  private setupForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.formGroup = this.formBuilder.group({
      origin: [null],
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
    });


    let previousValue = this.formGroup.value;
    this.formGroup.valueChanges.pipe(debounceTime(100)).subscribe((currentValue) => {
      if (
        previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart ||
        previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd ||
        previousValue.origin !== currentValue.origin
      ) {
        this.pager.page = 1;
      }
      this.getTrackingReport();
      this.getPieChartsData();
      this.getchartPerDayData();
      previousValue = currentValue;
    })
  }

  private getTrackingReport(): void {
    this.loadingContent = true;

    const formValues = this.formGroup.getRawValue();

    const filters: Record<string, unknown> = {
      origin: formValues.origin,
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.trackerService
      .getTrackingReport(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Tracker>) => {
          this.pager = pager;
          this.setupObservableSearch();
          this.persistListState(this.pager, this.searchString, this.searchParams);
        },
        complete: () => (this.loadingContent = false),
        error: (err) =>
          this.alertService.error(err.error?.message ?? this.t('report.tracking.unexpectedError')),
      });
  }

  private setupObservableSearch(): void {
    this.searchSubscription?.unsubscribe();

    this.listObservable = this.termOfSearch.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        this.searchString = term;
        return this.trackerService.getTrackingReport(1, this.pager.perPage, this.searchString, this.searchParams);
      })
    );

    this.searchSubscription = this.listObservable.subscribe({
      next: (pager: Pager<Tracker>) => {
        this.pager = pager;
        this.persistListState(this.pager, this.searchString, this.searchParams);
      },
      complete: () => (this.loadingContent = false),
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  search(term: string) {
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  searchByOrigin(origin: TrackerOriginEnum): void {
    if (origin) {
      this.originLabel = this.getTranslatedTracker(origin);
      this.formGroup.get('origin').setValue(origin);
    } else {
      this.originLabel = 'report.tracking.origin';
      this.formGroup.get('origin').setValue(null);
    }
  }

  public getTranslatedTracker(origin: TrackerOriginEnum | string): string {
    const origins: Record<TrackerOriginEnum, string> = {
      [TrackerOriginEnum.FACEBOOK]: 'Facebook',
      [TrackerOriginEnum.INSTAGRAM]: 'Instagram',
      [TrackerOriginEnum.GOOGLE]: 'Google',
      [TrackerOriginEnum.WHATSAPP]: 'WhatsApp',
      [TrackerOriginEnum.LINKEDIN]: 'LinkedIn',
      [TrackerOriginEnum.TIKTOK]: 'TikTok',
      [TrackerOriginEnum.WEBHOOK]: 'Webhook',
      [TrackerOriginEnum.OTHER]: this.t('report.tracking.originOther'),
    };
    return origins[origin];
  }

  public normalizeMediaTypeLabel(mediaType: string): string {
    const translations: Record<string, string> = {
      video: 'attendance.messageType.video',
      VIDEO: 'attendance.messageType.video',
      IMAGE: 'attendance.messageType.image',
      image: 'attendance.messageType.image',
    };
    const key = mediaType;
    return translations[key] ? this.t(translations[key]) : (key ? key.charAt(0).toUpperCase() + key.slice(1) : mediaType);
  }

  getPieChartsData(): void {
    this.trackerService.getPieChartsData(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: LeadCharts | Array<LeadCharts>) => {
          const chartData = Array.isArray(data) ? data[0] : data;
          this.pieChartByOrigin = chartData?.origin.map(item => ({
            ...item,
            label: this.getTranslatedTracker(item.label) ?? item.label,
          }));
          this.pieChartByMediaType = chartData?.mediaType.map(item => ({
            ...item,
            label: this.normalizeMediaTypeLabel(item.label) ?? item.label,
          })) || [];
          this.pieChartByCampaign = chartData?.campaign || [];
          this.buildPieCharts();
        },
        error: (err) => this.alertService.error(err.error?.message ?? this.t('report.tracking.unexpectedError')),
      });
  }

  getchartPerDayData(): void {
    this.trackerService.getPerDayChartData(this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          const result = Array.isArray(data) ? data[0] : data;
          this.chartPerDayData = result?.perDate ?? [];
          this.chartPeakHourData = result?.peakHour ?? [];
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayFormatted = DateUtil.toFormart(yesterday, 'dd/MM');
          this.leadsYesterday = this.chartPerDayData.find(d => d.date === yesterdayFormatted)?.value ?? 0;
          if (this.chartReady) {
            this.buildBarChart();
            this.buildPeakHourChart();
          }
        },
        error: (err) => this.alertService.error(err.error?.message ?? this.t('report.tracking.unexpectedError')),
      });
  }

  private buildBarChart(): void {
    this.cdr.detectChanges();
    if (!this.chartPerDayRef?.nativeElement) return;

    this.chartPerDayInstance?.destroy();
    this.chartPerDayInstance = null;
    if (!this.chartPerDayData.length) return;

    const ctx = this.chartPerDayRef.nativeElement.getContext('2d');
    if (!ctx) return;

    this.chartPerDayInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.chartPerDayData.map(d => d.date),
        datasets: [{
          label: this.t('report.tracking.chartLeadsPerDay'),
          data: this.chartPerDayData.map(d => d.value),
          backgroundColor: this.CHART_COLORS[0] + 'cc',
          borderColor: this.CHART_COLORS[0],
          borderWidth: 1,
          borderRadius: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => ` ${item.parsed.y} lead${item.parsed.y !== 1 ? 's' : ''}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 12 } },
          },
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
              font: { size: 12 },
            },
          },
        },
      },
    });
  }

  private buildPeakHourChart(): void {
    this.cdr.detectChanges();
    if (!this.chartPeakHourRef?.nativeElement) return;

    this.chartPeakHourInstance?.destroy();
    this.chartPeakHourInstance = null;
    if (!this.chartPeakHourData.length) return;

    const ctx = this.chartPeakHourRef.nativeElement.getContext('2d');
    if (!ctx) return;

    this.chartPeakHourInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.chartPeakHourData.map(d => d.label),
        datasets: [{
          label: this.t('report.tracking.chartLeads'),
          data: this.chartPeakHourData.map(d => d.value),
          backgroundColor: this.CHART_COLORS[1] + '33',
          borderColor: this.CHART_COLORS[1],
          borderWidth: 2,
          fill: true,
          tension: 0,
          pointRadius: 3,
          pointBackgroundColor: this.CHART_COLORS[1],
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => ` ${item.parsed.y} lead${item.parsed.y !== 1 ? 's' : ''}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 12 } },
          },
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
              font: { size: 12 },
            },
          },
        },
      },
    });
  }

  private buildPieCharts(): void {
    if (!this.chartReady) return;
    this.cdr.detectChanges();
    this.buildSinglePieChart(this.chartOriginRef, 'chartOriginInstance', this.pieChartByOrigin);
    this.buildSinglePieChart(this.chartMediaTypeRef, 'chartMediaTypeInstance', this.pieChartByMediaType);
    this.buildSinglePieChart(this.chartCampaignRef, 'chartCampaignInstance', this.pieChartByCampaign);
  }

  private buildSinglePieChart(
    ref: ElementRef<HTMLCanvasElement>,
    key: 'chartOriginInstance' | 'chartMediaTypeInstance' | 'chartCampaignInstance',
    data: Array<ChartSlice>
  ): void {
    if (!ref?.nativeElement) return;
    this[key]?.destroy();
    this[key] = null;
    if (!data.length) return;

    const ctx = ref.nativeElement.getContext('2d');
    if (!ctx) return;

    const total = data.reduce((sum, d) => sum + d.value, 0);

    this[key] = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          data: data.map(d => d.value),
          backgroundColor: this.CHART_COLORS.slice(0, data.length),
          borderWidth: 2,
          borderColor: '#fff',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { padding: 16, font: { size: 13 } },
          },
          tooltip: {
            callbacks: {
              label: (item) => ` ${item.label}: ${item.parsed} (${((item.parsed / total) * 100).toFixed(1)}%)`,
            },
          },
        },
      },
    });
  }

  get kpis(): Array<{ label: string; value: string | number }> {
    return [
      { label: 'report.tracking.kpiTotalLeads', value: this.pager?.total ?? 0 },
      { label: 'report.tracking.kpiTotalLeadsYesterday', value: this.leadsYesterday },
      { label: 'report.tracking.kpiLeadsInstagram', value: this.getOriginLeads('Instagram') },
      { label: 'report.tracking.kpiLeadsFacebook', value: this.getOriginLeads('Facebook') },
      { label: 'report.tracking.kpiLeadsWhatsApp', value: this.getOriginLeads('WhatsApp') },
      { label: 'report.tracking.kpiConversionInstagram', value: this.getOriginRate('Instagram') },
      { label: 'report.tracking.kpiConversionFacebook', value: this.getOriginRate('Facebook') },
      { label: 'report.tracking.kpiConversionWhatsApp', value: this.getOriginRate('WhatsApp') },
    ];
  }

  private getOriginLeads(label: string): number {
    return this.pieChartByOrigin.find(d => d.label === label)?.value ?? 0;
  }

  private getOriginRate(label: string): string {
    const total = this.pager?.total;
    if (!total) return '0%';
    return ((this.getOriginLeads(label) / total) * 100).toFixed(0) + '%';
  }

  onPeriodChange(event: MatSelectChange) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      if (period === 1) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(),
          dateEnd: new Date(),
        });
      } else if ([7, 15, 30, 60].includes(period)) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(today.setDate(today.getDate() - period)),
          dateEnd: new Date(),
        });
      }
      this.picker?.close();
    }
  }

  onPickerClose() {
    const dateStart = this.formGroup.get('dateRange')?.get('dateStart')?.value;
    const dateEnd = this.formGroup.get('dateRange')?.get('dateEnd')?.value;

    if (dateStart && dateEnd) {
    } else {
    }
  }

  openPicker(): void {
    this.isPicker = true;
    setTimeout(() => {
      this.picker.open();
    }, 300);
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getTrackingReport();
  }

  exportTrackerReportToExcel(): void {
    this.isLoading = true;
    this.fetchTrackersRecursively([], new Pager<Tracker>({ perPage: 500 }))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (trackers: Array<Tracker>) => {
          const formattedData = this.formatedTrackingReport(trackers);
          const columns = this.buildColumns([
            [this.t('report.tracking.exportColCreatedAt'), 'createdAt', 20],
            [this.t('report.tracking.exportColClient'), 'contact', 20],
            [this.t('report.tracking.exportColPhone'), 'phone', 20],
            [this.t('report.tracking.exportColOrigin'), 'origin', 20],
            [this.t('report.tracking.exportColTitle'), 'headline', 20],
            [this.t('report.tracking.exportColMainText'), 'copy', 80],
            [this.t('report.tracking.exportColMediaType'), 'mediaType', 20],
            [this.t('report.tracking.exportColAdLink'), 'adLink', 30],
            [this.t('report.tracking.exportColCreativeId'), 'sourceId', 30],
          ]);

          this.exportService.generateExcelReport(formattedData, columns, this.getReportFilters(), this.t('report.tracking.exportReportTitle'))
            .then(buffer => this.downloadExcelFile(buffer))
            .finally(() => {
              this.alertService.success(this.t('report.tracking.excelReportSuccess'));
              this.isLoading = false;
            });
        },
      });
  }

  exportTrackerReportToPDF(): void {
    this.isLoading = true;
    this.fetchTrackersRecursively([], new Pager<Tracker>({ perPage: 500 }))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (trackers: Array<Tracker>) => {
          const formattedData = this.formatedTrackingReport(trackers);
          const columns = this.buildColumns([
            [this.t('report.tracking.exportColCreatedAt'), 'createdAt', 80],
            [this.t('report.tracking.exportColClient'), 'contact', 80],
            [this.t('report.tracking.exportColPhone'), 'phone', 80],
            [this.t('report.tracking.exportColOrigin'), 'origin', 80],
            [this.t('report.tracking.exportColTitle'), 'headline', 80],
            [this.t('report.tracking.exportColMainText'), 'copy', 100],
            [this.t('report.tracking.exportColMediaType'), 'mediaType', 80],
            [this.t('report.tracking.exportColAdLink'), 'adLink', 80],
            [this.t('report.tracking.exportColCreativeId'), 'sourceId', 80],
          ]);

          this.exportService.generatePDFReport(formattedData, columns, this.getReportFilters(), this.t('report.tracking.exportReportTitle'))
            .finally(() => {
              this.alertService.success(this.t('report.tracking.pdfReportSuccess'));
              this.isLoading = false;
            });
        },
      });
  }

  private formatedTrackingReport(trackers: Array<Tracker>) {

    console.log('Trackers para exportação:', trackers);
    return trackers.map((tracker: Tracker) => {
      const ref = tracker.referral;
      return {
        createdAt: DateUtil.toFormart(tracker.createdAt, 'dd/MM/yyyy HH:mm:ss'),
        contact: tracker.contact?.name ?? '-',
        phone: tracker.contact?.phone ?? '-',
        origin: this.getTranslatedTracker(tracker.origin) ?? '-',
        headline: ref?.headline ?? ref?.title ?? '-',
        copy: !!ref?.body ? ref?.body : '-',
        mediaType: tracker.mediaType ? this.normalizeMediaTypeLabel(tracker.mediaType) : '-',
        adLink: ref?.source_url ?? ref?.sourceUrl ?? '-',
        sourceId: ref?.source_id ?? ref?.sourceId ?? '-',
      };
    });
  }

  private fetchTrackersRecursively(trackers: Array<Tracker>, pager: Pager<Tracker>): Observable<Array<Tracker>> {
    return this.trackerService.getTrackingReport(pager.page, pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        concatMap(newPager => {
          const allTrackers = [...trackers, ...newPager.list];
          if (allTrackers.length >= newPager.total) {
            return new Observable<Array<Tracker>>(observer => {
              observer.next(allTrackers);
              observer.complete();
            });
          } else {
            return this.fetchTrackersRecursively(
              allTrackers,
              { ...pager, page: pager.page + 1 }
            );
          }
        })
      );
  }

  private downloadExcelFile(buffer: any): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Trendmulti_Relatório_de_Trackeamento_${new Date().toLocaleDateString()}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private getReportFilters(): Record<string, string> {
    const values = this.formGroup.getRawValue();

    const filters: Record<string, string> = {};

    if (values.origin) {
      filters[this.t('report.tracking.origin')] = values.origin;
    }

    if (values.dateRange?.dateStart && values.dateRange?.dateEnd) {
      filters[this.t('report.tracking.period')] = `${DateUtil.toFormart(values.dateRange.dateStart, 'dd/MM/yyyy')} - ${DateUtil.toFormart(values.dateRange.dateEnd, 'dd/MM/yyyy')}`;
    }

    return filters;
  }

  private buildColumns(keys: Array<[string, string, number?]>): any[] {
    return keys.map(([header, key, width]) => ({
      header: header,
      key,
      width,
    }));
  }

  notify(tracker: Tracker): void {
    if (!!tracker.attendance) {
      this.router.navigate(['/attendance/panel'], { queryParams: { id: tracker.attendance._id, status: tracker.attendance.status } });
    }
  }

  private resolveFiltersCache(): void {
    if (!this.searchParams || !Object.keys(this.searchParams).length) return;

    if (this.searchParams['origin']) {
      const origin = this.searchParams['origin'] as TrackerOriginEnum;
      this.originLabel = this.getTranslatedTracker(origin);
      this.formGroup.get('origin')?.setValue(origin, { emitEvent: false });
    } else {
      this.originLabel = 'report.tracking.origin';
    }

    if (this.searchParams['dateStart'] && this.searchParams['dateEnd']) {
      this.formGroup.get('dateRange')?.setValue({
        dateStart: new Date(this.searchParams['dateStart'] as number),
        dateEnd: new Date(this.searchParams['dateEnd'] as number),
      }, { emitEvent: false });
    }

    if (this.searchString) {
      this.termOfSearch.next(this.searchString);
    }
  }
}
